import { useEffect, useCallback, useState } from "react";

/**
 * Custom hook for preventing accidental data loss when navigating away from forms with unsaved changes.
 *
 * Features:
 * 1. Blocks browser close/refresh with native browser dialog
 * 2. Provides state for showing a custom confirmation dialog when needed
 *
 * Note: This hook works with BrowserRouter. For internal SPA navigation blocking,
 * components should check isDirty before allowing navigation (e.g., in back button handlers).
 *
 * @param {boolean} isDirty - Whether the form has unsaved changes
 * @param {string} message - Optional custom message for the browser's beforeunload dialog
 * @returns {object} { isDirty - pass-through of the dirty state }
 *
 * @example
 * const { isDirty } = useUnsavedChangesWarning(hasChanges);
 *
 * // In your component, check isDirty before navigation:
 * const handleBack = () => {
 *   if (isDirty) {
 *     setShowWarning(true);
 *   } else {
 *     navigate('/back');
 *   }
 * };
 */
export function useUnsavedChangesWarning(
  isDirty,
  message = "יש שינויים שלא נשמרו. האם אתה בטוח שברצונך לצאת?"
) {
  // Block browser close/refresh with native browser dialog
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        // Modern browsers ignore custom message but still show a generic dialog
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, message]);

  return {
    isDirty,
  };
}

/**
 * Helper hook for tracking form dirty state by comparing current data with initial data.
 * Uses JSON.stringify for deep comparison.
 *
 * @param {object} currentData - Current form data
 * @param {object} initialData - Initial form data to compare against
 * @returns {boolean} Whether the form has changed (is dirty)
 *
 * @example
 * const isDirty = useFormDirtyState(formData, initialFormData);
 */
export function useFormDirtyState(currentData, initialData) {
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const hasChanges =
      JSON.stringify(currentData) !== JSON.stringify(initialData);
    setIsDirty(hasChanges);
  }, [currentData, initialData]);

  return isDirty;
}

export default useUnsavedChangesWarning;
